/**
 * Pure-function logic for Onboarding permission resolution.
 *
 * Onboarding - All: manager scope — view/edit/delete/execute all workplace onboardings.
 * Onboarding - My: employee self-service — view and edit only the user's own onboarding
 * (during EmployeeOnboardingProgress). Delete and execute are not applicable for My.
 */

import { OnboardingRecord } from 'core/API/onboarding';
import {
	SecurityRolePermission,
	hasModuleViewPermission,
	hasModuleEditPermission,
	hasModuleDeletePermission,
	hasModuleExecutePermission,
} from 'tests/services/permissionService';

export const ONBOARDING_MY_LABEL = 'Onboarding - My';
export const ONBOARDING_ALL_LABEL = 'Onboarding - All';

export interface OnboardingPermissions {
	canView: boolean;
	canViewAll: boolean;
	canViewOwn: boolean;
	canEditOwn: boolean;
	canEdit: boolean;
	canDelete: boolean;
	canExecute: boolean;
}

/**
 * Derives the module-level Onboarding permission set from a SecurityRolePermission array.
 */
export function resolveOnboardingPermissions(
	perms: SecurityRolePermission[],
): OnboardingPermissions {
	const hasAll = hasModuleViewPermission(perms, ONBOARDING_ALL_LABEL);
	const hasMy = hasModuleViewPermission(perms, ONBOARDING_MY_LABEL);

	return {
		canView: hasAll || hasMy,
		canViewAll: hasAll,
		canViewOwn: hasMy,
		canEditOwn: hasMy && hasModuleEditPermission(perms, ONBOARDING_MY_LABEL),
		canEdit: hasAll && hasModuleEditPermission(perms, ONBOARDING_ALL_LABEL),
		canDelete: hasAll && hasModuleDeletePermission(perms, ONBOARDING_ALL_LABEL),
		canExecute: hasAll && hasModuleExecutePermission(perms, ONBOARDING_ALL_LABEL),
	};
}

export function canViewOnboardingModule(perms: SecurityRolePermission[]): boolean {
	return resolveOnboardingPermissions(perms).canView;
}

export function canViewOwnOnboardingProgress(permissions: OnboardingPermissions): boolean {
	return permissions.canViewOwn;
}

export function canEditOwnOnboardingProgress(permissions: OnboardingPermissions): boolean {
	return permissions.canEditOwn;
}

export function getCurrentUserId(): number | null {
	try {
		const app =
			typeof window !== 'undefined' && localStorage.getItem('BYOD-app')
				? JSON.parse(localStorage.getItem('BYOD-app') as string)
				: null;
		const idFromApp =
			app && app.user && (app.user.id || app.user['@id']) ? app.user.id || app.user['@id'] : null;
		if (idFromApp) return Number(idFromApp);
		const idFromLS = localStorage.getItem('userId');
		if (idFromLS) return Number(idFromLS);
	} catch {
		// ignore
	}
	return null;
}

export function extractUserIdFromReference(ref: unknown): number | null {
	if (!ref) return null;
	if (typeof ref === 'object') {
		const obj = ref as { id?: number | string; '@id'?: string };
		if (obj['@id']) {
			const match = String(obj['@id']).match(/(\d+)$/);
			if (match) return Number(match[1]);
		}
		if (obj.id !== undefined && obj.id !== null) {
			const parsed = Number(obj.id);
			return Number.isNaN(parsed) ? null : parsed;
		}
	}
	if (typeof ref === 'string') {
		const match = ref.match(/(\d+)$/);
		if (match) return Number(match[1]);
	}
	return null;
}

export function isOnboardingForUser(
	record: OnboardingRecord,
	userId: number | null,
): boolean {
	if (!userId) return false;
	const employeeId = extractUserIdFromReference(record.user);
	return employeeId === userId;
}

export function canViewOnboardingRecord(
	permissions: OnboardingPermissions,
	record: OnboardingRecord,
	currentUserId: number | null,
): boolean {
	if (!permissions.canView) return false;
	if (permissions.canViewAll) return true;
	return permissions.canViewOwn && isOnboardingForUser(record, currentUserId);
}

export function canEditOnboardingRecord(
	permissions: OnboardingPermissions,
	record: OnboardingRecord,
	currentUserId: number | null,
): boolean {
	return canViewOnboardingRecord(permissions, record, currentUserId) && permissions.canEdit;
}

export function canDeleteOnboardingRecord(
	permissions: OnboardingPermissions,
	record: OnboardingRecord,
	currentUserId: number | null,
): boolean {
	return permissions.canViewAll && permissions.canDelete;
}

export function canExecuteOnboardingRecord(
	permissions: OnboardingPermissions,
	record: OnboardingRecord,
	currentUserId: number | null,
): boolean {
	return permissions.canViewAll && permissions.canExecute;
}

export function canStartOnboarding(permissions: OnboardingPermissions): boolean {
	return permissions.canEdit;
}

export function filterVisibleOnboardingRecords(
	records: OnboardingRecord[],
	permissions: OnboardingPermissions,
	currentUserId: number | null,
): OnboardingRecord[] {
	if (permissions.canViewAll) return records;
	if (!permissions.canViewOwn) return [];
	return records.filter((record) => isOnboardingForUser(record, currentUserId));
}
